import React, { useContext, useEffect, useMemo, useRef } from 'react';
import ObjectiveTabLabel from './ObjectiveTabLabel';
import ScrollArrow from '@/features/datasets/components/DataSelection/ScrollArrow';
import { GlobalContext } from '@/app/providers/globalState';
import { useEvaluationPresence } from '@/features/tasks/hooks/useEvaluationPresence';
import { computeActionStatusById } from './taskActionProgressUtils';
import { useHorizontalScrollArrows } from '@/shared/hooks/useHorizontalScrollArrows';
import '@/features/tasks/styles/ObjectiveTabsScrollable.css';

/**
 * Task tab strip — one tab per objective in the sub-group.
 *
 * Selecting a tab sets the active usecase id; downstream pages keep keying
 * off `currentUsecaseId`.
 *
 * Always renders the Spark-aligned custom horizontal strip (no Ant Design Tabs).
 * Optional `scrollable` enables left/right overflow arrows.
 * Optional `renderPanel` renders the active objective body below the strip.
 * Optional `showStepProgressOnTabs` shows four micro-dots per tab (Task landing).
 */
const ObjectiveTabs = ({
    usecases = [],
    activeUsecaseId,
    onChange,
    errorByUsecaseId = {},
    renderPanel,
    tabBarExtraContent,
    style,
    scrollable = false,
    showStepProgressOnTabs = false,
}) => {
    const { trainStatuses, progressStatus, evaluationResultsReady, realtimeResyncTick } =
        useContext(GlobalContext);
    const evalPresenceByUsecaseId = useEvaluationPresence({
        usecases,
        evaluationResultsReady,
        realtimeResyncTick,
        enabled: showStepProgressOnTabs && usecases.length > 1,
    });

    const activeKey = String(activeUsecaseId || usecases[0]?._id || '');

    const usecasePresenceKey = useMemo(
        () =>
            usecases
                .map((u) => `${u?._id}:${u?.client_id}`)
                .sort()
                .join('|'),
        [usecases]
    );

    const actionStatusByUsecaseId = useMemo(() => {
        if (!showStepProgressOnTabs || usecases.length <= 1) return null;
        const map = {};
        for (const uc of usecases) {
            const id = String(uc._id);
            const { actionStatusById } = computeActionStatusById(uc, {
                trainStatuses,
                progressStatus,
                evaluationResultsReady,
                hasEvaluationResults: !!evalPresenceByUsecaseId[id],
            });
            map[id] = actionStatusById;
        }
        return map;
    }, [
        usecases,
        trainStatuses,
        progressStatus,
        evaluationResultsReady,
        evalPresenceByUsecaseId,
        showStepProgressOnTabs,
    ]);

    const scrollDeps = useMemo(
        () => [usecases.length, activeKey, usecasePresenceKey],
        [usecases.length, activeKey, usecasePresenceKey]
    );
    const { scrollContainerRef, showLeftArrow, showRightArrow, scroll } =
        useHorizontalScrollArrows(scrollDeps);

    const activeTabRef = useRef(null);
    useEffect(() => {
        if (!scrollable) return;
        const container = scrollContainerRef.current;
        const btn = activeTabRef.current;
        if (!container || !btn) return;
        const containerRect = container.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        const margin = 16;
        if (btnRect.left < containerRect.left) {
            container.scrollBy({
                left: btnRect.left - containerRect.left - margin,
                behavior: 'smooth',
            });
        } else if (btnRect.right > containerRect.right) {
            container.scrollBy({
                left: btnRect.right - containerRect.right + margin,
                behavior: 'smooth',
            });
        }
    }, [scrollable, activeKey, scrollContainerRef]);

    const activeUsecase = useMemo(
        () => usecases.find((uc) => String(uc._id) === activeKey) || usecases[0] || null,
        [usecases, activeKey]
    );

    if (usecases.length === 0) return null;

    return (
        <div className="objective-tabs-scrollable" style={style}>
            <div className="objective-tabs-scrollable__nav">
                {scrollable && showLeftArrow && (
                    <ScrollArrow
                        direction="left"
                        onClick={() => scroll('left')}
                        compact
                    />
                )}
                <div
                    ref={scrollContainerRef}
                    className="objective-tabs-scrollable__list"
                    role="tablist"
                >
                    {usecases.map((uc) => {
                        const id = String(uc._id);
                        const isActive = id === activeKey;
                        return (
                            <button
                                key={id}
                                ref={isActive ? activeTabRef : null}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={
                                    isActive
                                        ? 'objective-tabs-scrollable__tab objective-tabs-scrollable__tab--active'
                                        : 'objective-tabs-scrollable__tab'
                                }
                                onClick={() => onChange(id)}
                            >
                                <ObjectiveTabLabel
                                    usecase={uc}
                                    hasError={!!errorByUsecaseId[id]}
                                    isActive={isActive}
                                    actionStatusById={
                                        actionStatusByUsecaseId?.[id] ?? null
                                    }
                                />
                            </button>
                        );
                    })}
                    {tabBarExtraContent ? (
                        <div className="objective-tabs-scrollable__extra">
                            {tabBarExtraContent}
                        </div>
                    ) : null}
                </div>
                {scrollable && showRightArrow && (
                    <ScrollArrow
                        direction="right"
                        onClick={() => scroll('right')}
                        compact
                    />
                )}
            </div>
            {renderPanel && activeUsecase ? renderPanel(activeUsecase) : null}
        </div>
    );
};

export default ObjectiveTabs;
